interface OntologyListType {
  data: MockObjectItem[]; // 真正的数组在这里
}import { useOntologyList, useObjectSemanticMapping } from '@/api/semanticDataMap';
import {
  MockObjectItem,
  ObjectBasicInfo,
  ObjectMappingInfo,
  ObjectSemanticMappingResponse,
} from '@/types/semanticDataMap';
import { useState, useCallback, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

/**
 * 语义数据映射钩子
 * 提供对象列表、关系数据和映射数据的管理功能
 */
export const useSemanticDataMap = (ontologyId: number) => {
  // 状态管理
  const [searchText, setSearchText] = useState<string>('');
  const [selectedObjectId, setSelectedObjectId] = useState<string>('');
  const navigate = useNavigate();
  const SESSION_STORAGE_KEY = `semantic_data_map_selected_object_${ontologyId}`;

  // API 调用 - 对象列表
  const { data, isLoading, error, refetch } = useOntologyList(Number(ontologyId));
  console.log('useOntologyList', data);

  // API 调用 - 已选对象的语义与映射详情
  const {
    data: semanticMappingRaw,
    isLoading: semanticMappingLoading,
  } = useObjectSemanticMapping(selectedObjectId);

  // 确保 objects 始终有值
  const displayObjects = useMemo(() => {
    return data?.data && data.data.length > 0 ? data.data : [];
  }, [data?.data]);

  useEffect(() => {
    console.log('useEffect', SESSION_STORAGE_KEY, sessionStorage.getItem(SESSION_STORAGE_KEY), displayObjects);
    
    // 尝试从会话存储中读取之前选中的对象ID
    const savedSelectedObjectId = sessionStorage.getItem(SESSION_STORAGE_KEY);
    if (savedSelectedObjectId && displayObjects.some(obj => String(obj.id) === savedSelectedObjectId)) {
      // 如果保存的对象ID存在且在当前对象列表中，使用它
      setSelectedObjectId(savedSelectedObjectId);
    } else if (displayObjects.length > 0 && displayObjects[0]?.id) {
      // 否则，使用第一个对象的ID
      setSelectedObjectId(String(displayObjects[0].id));
    }
  }, [displayObjects, SESSION_STORAGE_KEY]);

  // 过滤后的对象列表
  const filteredObjects: MockObjectItem[] = useMemo(() => {
    
    if (!searchText.trim()) {
      return displayObjects;
    }
    return displayObjects?.filter(obj => 
      obj.name?.toLowerCase().includes(searchText.toLowerCase())
    );
  }, [displayObjects, searchText]);

  // 获取当前选中的对象
  const selectedObject = useMemo(() => {
    return displayObjects.find(obj => String(obj.id) === String(selectedObjectId)) || null;
  }, [displayObjects, selectedObjectId]);

  // 真实语义与映射数据
  const semanticMappingData: ObjectSemanticMappingResponse | null = useMemo(() => {
    return semanticMappingRaw?.data ?? null;
  }, [semanticMappingRaw]);

  const objectInfo: ObjectBasicInfo | null = semanticMappingData?.objectInfo ?? null;
  const mappingInfo: ObjectMappingInfo | null = semanticMappingData?.mappingInfo ?? null;

  // 操作函数
  const handleSearch = useCallback((text: string) => {
    setSearchText(text);
  }, []);

  const handleSelectObject = useCallback((id: string) => {
    setSelectedObjectId(id);
    sessionStorage.setItem(SESSION_STORAGE_KEY, id)
  }, []);

  const handleToggleEdit = useCallback((id: string) => {    
    navigate(`/data-map-editor?id=${id}&ontologyId=${ontologyId}`);
  }, []);

  const handleRefreshData = useCallback(() => {
    refetch();
  }, [refetch]);

  return {
    // 数据
    objects: displayObjects,
    filteredObjects,
    selectedObject,
    selectedObjectId,
    objectInfo,
    mappingInfo,
    semanticMappingLoading,
    
    // 状态
    isLoading,
    error,
    searchText,
    
    // 操作函数
    handleSearch,
    handleSelectObject,
    handleToggleEdit,
    handleRefreshData,
    
    // API 相关
    refetch,
  };
};

/**
 * 语义数据映射钩子的简化版本
 * 仅提供基础数据，适用于简单场景
 */
export const useSimpleSemanticDataMap = (ontologyId: number) => {
  const { objects, isLoading, error } = useSemanticDataMap(ontologyId);
  
  return {
    objects,
    isLoading,
    error,
  };
};
